import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';

interface UseListSearchOptions {
  /** Délai de debounce en millisecondes */
  debounceMs?: number;
  /** Valeur initiale */
  initialValue?: string;
  /**
   * Si true, persiste la valeur de recherche dans le query param de l'URL
   * (clé `paramName`, défaut "search"). Init depuis l'URL au mount, sync à
   * chaque setSearchTerm. Préserve les autres params.
   */
  persistInUrl?: boolean;
  /** Nom du query param URL utilisé si persistInUrl=true. Défaut "search". */
  paramName?: string;
}

interface UseListSearchReturn {
  /** Valeur de recherche immédiate (contrôlée par l'input) */
  searchTerm: string;
  /** Valeur de recherche debounced (pour les requêtes API) */
  debouncedSearch: string;
  /** Setter pour la valeur de recherche */
  setSearchTerm: (value: string) => void;
  /** Reset de la recherche */
  resetSearch: () => void;
  /** La recherche est-elle active ? */
  isSearchActive: boolean;
}

/**
 * Hook pour gérer la recherche avec debounce optionnel
 * Conforme au CONTEXT.md : utilise useMemo pour dérivation
 */
export function useListSearch(options: UseListSearchOptions = {}): UseListSearchReturn {
  const { debounceMs = 300, initialValue = '', persistInUrl = false, paramName = 'search' } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTermInternal] = useState(() => {
    if (persistInUrl) {
      const fromUrl = searchParams.get(paramName);
      if (fromUrl !== null) return fromUrl;
    }
    return initialValue;
  });

  const setSearchTerm = useCallback(
    (value: string) => {
      setSearchTermInternal(value);
      if (persistInUrl) {
        const next = new URLSearchParams(searchParams);
        if (value && value.length > 0) {
          next.set(paramName, value);
        } else {
          next.delete(paramName);
        }
        setSearchParams(next, { replace: true });
      }
    },
    [persistInUrl, paramName, searchParams, setSearchParams],
  );

  const debouncedSearch = useDebounce(searchTerm, debounceMs);

  const isSearchActive = useMemo(() => {
    return debouncedSearch.trim().length > 0;
  }, [debouncedSearch]);

  const resetSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  return {
    searchTerm,
    debouncedSearch,
    setSearchTerm,
    resetSearch,
    isSearchActive,
  };
}
